import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Portal } from 'react-portal';

import { SR_API_ACTION_INSURANCE_GET_TLP_CONTENT } from 'api/utilityActionTypes';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import useDispatch from 'client/hooks/useDispatch';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrMarkdown, { SR_MARKDOWN_THEME_UTILITY_PROFIT } from 'client/components/SrMarkdown';

import './UPTenantLiabilityLearnMoreModal.less';

export const TLP_LEARN_MORE_CONTENT_SLUG = 'liability-protection-enable-learn-more';

const UPTenantLiabilityLearnMoreModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    document.body.classList.add('overflow-hidden');
    return () => document.body.classList.remove('overflow-hidden');
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    let mounted = true;

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_INSURANCE_GET_TLP_CONTENT,
      payload: { slug: TLP_LEARN_MORE_CONTENT_SLUG },
      onSuccess: (data) => {
        if (!mounted) return;
        setContent(data);
        setIsLoading(false);
      },
      onError: (error) => {
        if (!mounted) return;
        setFetchError(error);
        setIsLoading(false);
      },
    });

    return () => {
      mounted = false;
    };
  }, [dispatch]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <SrText font="greycliff" color="gray" fontSize="14px">
          Loading…
        </SrText>
      );
    }
    if (fetchError || !content) {
      return (
        <SrText font="greycliff" color="red" fontSize="14px">
          Unable to load content. Please try again.
        </SrText>
      );
    }
    if (content.format !== 'markdown') {
      return (
        <SrText font="greycliff" color="blueGreycliff" fontSize="14px">
          {content.body}
        </SrText>
      );
    }
    return <SrMarkdown source={content.body} theme={SR_MARKDOWN_THEME_UTILITY_PROFIT} />;
  };

  return (
    <Portal>
      <SrBox
        alignItems="flex-start"
        backgroundColor="rgba(25, 51, 64, 0.96)"
        bottom="0"
        display="flex"
        justifyContent="center"
        left="0"
        onClick={onClose}
        overflow="auto"
        padding="10vh 0"
        position="fixed"
        right="0"
        styleName="overlay"
        top="0"
        zIndex="1000"
      >
        <SrBox
          backgroundColor="#223C47"
          height="10vh"
          left="0"
          pointerEvents="none"
          position="fixed"
          right="0"
          styleName="topOverlayMask"
          top="0"
          zIndex="10"
        />
        <SrBox
          backgroundColor="#223C47"
          bottom="0"
          height="10vh"
          left="0"
          pointerEvents="none"
          position="fixed"
          right="0"
          styleName="bottomOverlayMask"
          zIndex="10"
        />
        <SrBox
          backgroundColor="white"
          borderRadius="14px"
          display="flex"
          flexDirection="column"
          maxWidth="600px"
          onClick={(e) => e.stopPropagation()}
          position="relative"
          styleName="modalBox"
          width="100%"
          minHeight={600}
        >
          <SrBox
            alignItems="center"
            backgroundColor="white"
            borderRadius="14px 14px 0 0"
            display="flex"
            justifyContent="space-between"
            padding="40px 40px 24px"
            position="sticky"
            top="0"
            zIndex="1"
          >
            <SrFlex align="center" gap="12px">
              <SrBox
                backgroundImage="url(/images/simplecover-shield-icon.png)"
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
                backgroundSize="contain"
                flexShrink="0"
                height="32px"
                width="32px"
              />
              <SrText font="greycliff" color="blueGreycliff" weight="600" fontSize="26px">
                Tenant Liability Protection
              </SrText>
            </SrFlex>
            <button
              aria-label="Close modal"
              onClick={onClose}
              style={{
                alignItems: 'center',
                background: 'none',
                border: 'none',
                color: '#666',
                cursor: 'pointer',
                display: 'flex',
                fontSize: '28px',
                height: '32px',
                justifyContent: 'center',
                lineHeight: 1,
                padding: 0,
                width: '32px',
              }}
              styleName="closeButton"
              type="button"
            >
              &times;
            </button>
          </SrBox>

          <SrBox backgroundColor="white" borderRadius="0 0 14px 14px" padding="0 40px 40px">
            {renderBody()}
          </SrBox>
        </SrBox>
      </SrBox>
    </Portal>
  );
};

UPTenantLiabilityLearnMoreModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

UPTenantLiabilityLearnMoreModal.displayName = 'UPTenantLiabilityLearnMoreModal';

export default UPTenantLiabilityLearnMoreModal;
